import axios from 'axios';
import { isStepUpRequired } from '../../lib/stepUp';
import { createOwnerOperationsApi } from '../contacts/ownerOperationsApi';

/**
 * Client for /api/list-management (Epic #767).
 *
 * Every list call sends a bounded `page_size` (≤ 100): the server clamps it
 * anyway, but the UI never asks for an oversized page (#768, #770).
 */
const ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const BASE = `${ROOT}/list-management`;

export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const MAX_PAGE_SIZE = 100;

export function clampPageSize(size) {
    const n = Number(size) || 25;
    return Math.max(1, Math.min(MAX_PAGE_SIZE, n));
}

// `source_columns` names the file columns whose values a rows page should carry
// (#974). It is sent as a REPEATED query parameter (`?source_columns=a&source_columns=b`),
// each header verbatim, so a header that contains a comma survives. The server
// accepts at most 50 and refuses (422) a list over that, so a caller that has more
// must say so itself: this never hides the cut silently (see `overSourceColumnCap`).
export const MAX_SOURCE_COLUMNS = 50;
export const overSourceColumnCap = (columns) => (Array.isArray(columns) ? columns.length : 0) > MAX_SOURCE_COLUMNS;
export function sourceColumnsParam(columns) {
    const list = (Array.isArray(columns) ? columns : []).filter((h) => typeof h === 'string' && h !== '');
    return list.length ? { source_columns: list.slice(0, MAX_SOURCE_COLUMNS) } : {};
}

// Axios writes an array as `key[]=a&key[]=b` by default; the rows route reads the
// repeated plain key. `indexes: null` drops the brackets.
export const REPEATED_KEY_PARAMS = { indexes: null };

export function createLmApi(getAuthHeader) {
    const cfg = (extra = {}) => ({ ...extra, headers: { ...(getAuthHeader ? getAuthHeader() : {}), ...(extra.headers || {}) } });
    const get = (path, params, signal, extra = {}) => axios.get(`${BASE}${path}`, cfg({ params, signal, ...extra })).then((r) => r.data);
    const post = (path, body) => axios.post(`${BASE}${path}`, body ?? {}, cfg()).then((r) => r.data);
    const ownerOperations = createOwnerOperationsApi(getAuthHeader);

    return {
        summary: () => get('/summary'),
        fields: () => get('/fields'),
        sources: () => get('/sources'),
        owners: () => get('/owners'),

        rows: (params, signal) => {
            const { source_columns: sourceColumns, ...rest } = params;
            return get('/rows', { ...rest, page_size: clampPageSize(params.page_size), ...sourceColumnsParam(sourceColumns) }, signal,
                       { paramsSerializer: REPEATED_KEY_PARAMS });
        },
        row: (rowId) => get(`/rows/${encodeURIComponent(rowId)}`),
        patchRow: (rowId, stagedValues) =>
            axios.patch(`${BASE}/rows/${encodeURIComponent(rowId)}`, { staged_values: stagedValues }, cfg()).then((r) => r.data),
        bulk: (action, body) => post(`/rows/${action}`, body),
        compareCandidate: (rowId, targetId) => get(`/rows/${encodeURIComponent(rowId)}/compare`, { target_id: targetId }),

        // `migration` ({operationId, partIndex}) uploads one part of the one-time
        // Contact Owner migration (#862); without it the upload is a standard import.
        uploadCsv: (file, migration) => {
            const form = new FormData();
            form.append('file', file);
            if (migration) {
                form.append('mode', 'owner_migration');
                form.append('parent_operation_id', migration.operationId);
                form.append('part_index', String(migration.partIndex));
            }
            return axios.post(`${BASE}/imports/csv`, form, cfg()).then((r) => r.data);
        },
        importPitchbook: (entity) => post('/imports/pitchbook', { entity }),
        saveMapping: (importId, body) =>
            axios.put(`${BASE}/imports/${encodeURIComponent(importId)}/mapping`, body, cfg()).then((r) => r.data),
        preview: (importId) => post(`/imports/${encodeURIComponent(importId)}/preview`),
        confirm: (importId, body) => post(`/imports/${encodeURIComponent(importId)}/confirm`, body),
        discardDraft: (importId) =>
            axios.delete(`${BASE}/imports/${encodeURIComponent(importId)}/draft`, cfg()).then((r) => r.data),
        // Create a global Contact/Company custom field from Map Fields (#972); the same
        // admin route as Admin → Custom Fields, so its 403/400 answers are unchanged.
        createCustomField: (body) =>
            axios.post(`${ROOT}/admin/custom-fields`, body, cfg()).then((r) => r.data),

        imports: (params, signal) => get('/imports', { ...params, page_size: clampPageSize(params.page_size) }, signal),
        importDetail: (importId) => get(`/imports/${encodeURIComponent(importId)}`),
        deleteImport: (importId, reason) =>
            axios.delete(`${BASE}/imports/${encodeURIComponent(importId)}`, cfg({ data: { reason } })).then((r) => r.data),
        restoreImport: (importId) => post(`/imports/${encodeURIComponent(importId)}/restore`),

        repDistribution: () => get('/pool/rep-distribution'),
        repBalanceSuggest: (userIds) => post('/pool/rep-balance/suggest', userIds ? { user_ids: userIds } : {}),

        commitPreview: (selection) => post('/commit/preview', selection),
        commit: (previewId, idempotencyKey) => post('/commit', { preview_id: previewId, idempotency_key: idempotencyKey }),
        job: (jobId, params) => get(`/commit/jobs/${encodeURIComponent(jobId)}`, { ...params, page_size: clampPageSize(params?.page_size) }),
        cancelJob: (jobId) => post(`/commit/jobs/${encodeURIComponent(jobId)}/cancel`),
        retryJob: (jobId, rowIds) => post(`/commit/jobs/${encodeURIComponent(jobId)}/retry`, rowIds ? { row_ids: rowIds } : {}),
        // A migration part's revert needs a reason (#862); ordinary jobs ignore it.
        revertJob: (jobId, reason) =>
            post(`/commit/jobs/${encodeURIComponent(jobId)}/revert`, reason ? { reason } : {}),

        // The one-time Contact Owner migration (#862).
        createOwnerMigration: (body) => post('/owner-migrations', body),
        ownerMigration: (operationId, { staleOwner = false } = {}) =>
            get(`/owner-migrations/${encodeURIComponent(operationId)}`, staleOwner ? { include: 'stale_owner' } : undefined),
        requestMigrationApproval: (operationId, reason) =>
            post(`/owner-migrations/${encodeURIComponent(operationId)}/request-approval`, { reason }),
        rollbackMigration: (operationId, reason) =>
            post(`/owner-migrations/${encodeURIComponent(operationId)}/rollback`, { reason }),
        // Approvals are the shared Contact Owner operations routes (not List Management's).
        approveOperation: (operationId, reason) => ownerOperations.approve(operationId, reason),
        rejectOperation: (operationId, reason) => ownerOperations.reject(operationId, reason),

        enrichPreview: (selection) => post('/rows/enrich/preview', selection),
        enrichApply: (suggestions) => post('/rows/enrich/apply', { suggestions }),
        settings: () => get('/settings'),
        saveSettings: (body) => axios.put(`${BASE}/settings`, body, cfg()).then((r) => r.data),
    };
}

/** A per-dialog idempotency key, so a double-click confirms once (#774). */
export function newIdempotencyKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `lm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Messages for codes that may arrive without a `detail.message` (#862). */
const ERROR_MESSAGES = {
    migration_approval_required: 'Owner migration rows commit only after a different admin approves the migration.',
    migration_exceptions: 'Resolve every exception before requesting approval.',
    migration_part_conflict: 'This file is already a live import of another kind or migration part.',
    mixed_import_modes: 'Commit owner-migration rows separately from ordinary imports.',
    operation_not_pending: 'This request is no longer waiting for a decision.',
    approver_must_differ: 'Someone other than the requester must approve this.',
    idempotency_key_reused: 'That request was already sent with different details.',
    reason_required: 'A reason is required.',
    job_running: 'Wait for the running part to finish first.',
};

/** Human message for an API error, preferring the server's `detail.message`. */
export function lmError(err, fallback = 'Something went wrong. Please try again.') {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    if (detail?.error && ERROR_MESSAGES[detail.error]) return ERROR_MESSAGES[detail.error];
    if (detail?.error) return detail.error.replace(/_/g, ' ');
    if (!err?.response && err?.message && !/^(Network Error|Request failed)/.test(err.message)) return err.message;
    return fallback;
}

export function lmErrorCode(err) {
    const detail = err?.response?.data?.detail;
    return (detail && typeof detail === 'object' && detail.error) || null;
}

/** Refused for any reason but the plan gate or the missing code factor. The
 *  MFA 403 is its own state (`isStepUpRequired`, #854): it needs a code, not
 *  different permissions. */
export function isUnauthorized(err) {
    const s = err?.response?.status;
    if (isStepUpRequired(err)) return false;
    return s === 401 || (s === 403 && lmErrorCode(err) !== 'feature_not_available');
}
